import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { PAST_DAYS } from '@/constants/general';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { SurgeRateV2 } from '../api/types';

// Surge rates — only meaningful when the billing account's customer has
// surge pricing enabled. Mirrors the legacy CreateGigOrder flow which
// fetches `surgeRatesFromDate(today - PAST_DAYS)` so tenant-admin
// backdated shifts can also be auto-marked. We only fire when the flag
// is true to avoid an unnecessary round-trip; the endpoint
// short-circuits to `[]` when `enable_surge_rate` is false anyway.
const useSurgeRates = (
  billingAccountId: number | null,
  surgeEnabled: boolean,
) => {
  const enabled = billingAccountId != null && surgeEnabled;

  // Stable per-mount `from_date` so the query key is stable across
  // renders within a session. The legacy hook computed this inside the
  // effect once per billing-account change; we replicate that by
  // memoising on the same inputs.
  const fromDate = useMemo(() => {
    const floor = new Date();
    floor.setDate(floor.getDate() - PAST_DAYS);
    return floor.toISOString().slice(0, 10);
  }, [billingAccountId, surgeEnabled]);

  const query = useQuery<SurgeRateV2[]>({
    queryKey: enabled
      ? orderV2QueryKeys.surgeRates(billingAccountId, fromDate)
      : ['orders_v2', 'surge_rates', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .listSurgeRates(billingAccountId as number, fromDate)
        .then((rates) => rates || []),
    enabled,
  });

  return { surgeRates: enabled ? (query.data ?? []) : [] };
};

export default useSurgeRates;
